import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import HandshakeIcon from '@mui/icons-material/Handshake';
import InfoIcon from '@mui/icons-material/Info';
import { getUserNameById } from '../../../UserContext.js';
import {
  isOfferSent,
  wereAnyOffersSent,
  isCaseSettled,
  hasUpdatedOffer,
  getSettlementInfo,
  isMasterPetitionCase,
  isAdminHoldDetermination,
  isAssignmentComplete,
} from '../utils/helpers.js';
import { calculateWeightedAverage, CASE_REVIEW_COLUMNS } from '../../../../common/mappings.js';
import { getConversionFactor } from '../../../../utils/conversionUtils.js';
import { getCompAdjustedPrice } from '../../../../utils/adjustmentCalculator.js';
import { getCaseStrengthPercentage, getCaseStrengthColor } from '../../../../utils/caseStrengthCalculator.js';
import { getIFMVUtil, getAVUtil } from '../../../../utils/getAVUtil.js';
import { getMostRecentOffer, getMostRecentOfferValue } from '../../../../utils/mostRecentOfferUtils.js';
import { formatCourtDateDisplayDay } from '../../../../common/dateUtils.js';
import logger from '../../../../utils/logger';
import { usePersistedDataStore } from '../../../store.js';
import { findCompIndex } from '../../common/compSheetLookup.js';
import { savedCompIds } from '../../common/compAddressLookup.js';
import { openDocRequestTooltipText } from '../components/DocRequestChip.js';
import { displayFileLabel, EVIDENCE_PARTIES, filterNyscefFilesByParty, getNyscefRawFilename } from '../../NyscefUpload/nyscefExhibitUtils.js';
// import { apply75PercentThresholdRoundingAV } from '../../../../utils/offerThresholdUtils';

const isAwaitingDecisionCase = (caseItem) => {
  const actionField = isMasterPetitionCase(caseItem)
    ? caseItem?.Art7DeterminationAction
    : caseItem?.SCARDeterminationAction;
  return String(actionField ?? '').trim() === 'D';
};

const getEffectiveDeterminationAction = (caseItem) => {
  const raw = isMasterPetitionCase(caseItem)
    ? caseItem?.Art7DeterminationAction
    : caseItem?.SCARDeterminationAction;
  return (raw == null ? '' : String(raw)).trim();
};

const isBarOnlyCase = (caseItem) =>
  !isMasterPetitionCase(caseItem) &&
  (caseItem?.SCARFiled === 0 || caseItem?.SCARFiled === '0');

const readStipEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const status = String(entry.status ?? '').trim();
  return status ? entry : null;
};

/** BAR stip if set, otherwise SCAR. */
const getActiveStip = (caseItem) => {
  const info = caseItem?.StipInfo;
  return readStipEntry(info?.bar) || readStipEntry(info?.scar);
};

const getEffectiveStipStatus = (caseItem) => getActiveStip(caseItem)?.status || '';

export const isStipFinalizedCase = (caseItem) => {
  const status = getEffectiveStipStatus(caseItem);
  return status === 'Countersigned' || status === 'SoOrdered';
};

const getNotesDisabledReason = ({ awaitingDecision, stipFinalized, caseSettled, adminHold }) => {
  if (adminHold) return 'Admin hold';
  if (awaitingDecision) return 'Awaiting decision';
  if (stipFinalized) return 'Countersigned';
  if (caseSettled) return 'Case settled';
  return null;
};

// Helper function to check if there is a town offer in the most recent round
const hasTownOfferInMostRecentRound = (negotiations) => {
  if (!negotiations || !Array.isArray(negotiations) || negotiations.length === 0) return false;
  
  // Sort negotiations by round number (descending) to get most recent first
  const sortedRounds = [...negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
  
  // Check the most recent round for a town offer
  const mostRecentRound = sortedRounds[0];
  if (mostRecentRound && mostRecentRound.offers && Array.isArray(mostRecentRound.offers)) {
    const townOffer = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Town');
    return townOffer && townOffer.Offer !== null && townOffer.Offer !== undefined && townOffer.Offer !== '';
  }
  
  return false;
};

export const calculateTop5Average = (compPrices) => {
  if (!compPrices || compPrices.length === 0) return 0;
  
  // Take top 5 comps (first 5 in the array)
  const top5Prices = compPrices.slice(0, 5).filter(price => 
    price !== null && price !== undefined && !isNaN(parseFloat(price))
  );
  
  if (top5Prices.length === 0) return 0;
  
  const sum = top5Prices.reduce((acc, price) => acc + parseFloat(price), 0);
  return sum / top5Prices.length;
};

export const calculateTop10WeightedAverage = (compSheet, savedCompIds) => {
  if (!compSheet || !savedCompIds || savedCompIds.length === 0) return 0;
  
  // Get all comp prices from the comp sheet (top 10)
  const allCompPrices = [];
  const maxComps = Math.min(10, compSheet.parcel_id?.length || 0);
  
  for (let i = 0; i < maxComps; i++) {
    const adjPrice = compSheet.adj_price?.[i];
    if (adjPrice !== undefined && adjPrice !== null && !isNaN(parseFloat(adjPrice))) {
      allCompPrices.push(parseFloat(adjPrice));
    }
  }
  
  return calculateWeightedAverage(allCompPrices);
};

const positivePrice = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Adjusted price when the top comp is the subject and that price is at or below IFMV, otherwise null. */
export const subjectSalePrice = (caseItem, compSheet) => {
  if (usePersistedDataStore.getState().negotiationObj?.reportType === 'assessment') return null;
  const pid0 = compSheet?.parcel_id?.[0];
  const pid1 = compSheet?.parcel_id?.[1];
  if (pid0 == null || pid1 == null || String(pid0) !== String(pid1)) return null;
  const price = positivePrice(compSheet?.adj_price?.[1]);
  if (price == null) return null;
  const ifmv = positivePrice(getIFMVUtil(compSheet, 0) ?? getIFMVUtil(caseItem));
  if (ifmv != null && price > ifmv) return null;
  return Math.round(price);
};

/** Town-view grid: workup column shows case `SubjectMarketValue`, unless the top comp is the subject. */
export const workupValueFromSubjectMarketValue = (caseItem, compSheet) => {
  const salePrice = subjectSalePrice(caseItem, compSheet);
  if (salePrice != null) return salePrice;
  const raw = caseItem?.SubjectMarketValue;
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const n = parseFloat(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Propriety Workup: weighted average of the saved comps' adjusted prices, recomputed from the raw
 * SalePrice/IFMV with property, time, and misc adjustments (same calculation as the case review toolbar).
 */
export const calculateProprietyWorkup = (caseItem, compSheet) => {
  if (caseItem?.parcel_id != null) {
    const salePrice = subjectSalePrice(caseItem, compSheet);
    if (salePrice != null) return salePrice;
  }

  const compIds = savedCompIds(caseItem.Comps);
  if (!compSheet || !compIds.length) return 0;

  const reportType = usePersistedDataStore.getState().negotiationObj?.reportType;
  const priceField = reportType === 'assessment' ? 'IFMV' : 'SalePrice';
  const validPrices = [];
  let missingComps = 0;
  compIds.forEach((compId) => {
    const parcelIndex = findCompIndex(compSheet, compId, reportType);
    if (parcelIndex < 0) { missingComps++; return; }
    const basePrice = parseFloat(compSheet[priceField]?.[parcelIndex]);
    if (!Number.isFinite(basePrice)) return;
    const adjustedPrice = getCompAdjustedPrice({
      salePrice: basePrice,
      basePrice,
      miscAdjustments: caseItem.MiscAdjustments || {},
      savedComps: compIds,
      currentCompId: compId,
      memoizedCompObject: compSheet,
      parcelIndex,
      logType: 'PROPRIETY_WORKUP',
    });
    if (Number.isFinite(adjustedPrice)) validPrices.push(adjustedPrice);
  });

  if (validPrices.length) return calculateWeightedAverage(validPrices);
  return missingComps ? 'loading' : 0;
};


// Helper function to get the assignee for the current priority status
const getCurrentAssignee = (caseItem, currentPriority) => {
  if (!caseItem.CaseAssignmentInfo || !Array.isArray(caseItem.CaseAssignmentInfo)) {
    return null;
  }
  
  // Find the assignment info for the current priority review type
  const assignment = caseItem.CaseAssignmentInfo.find(
    info => info.ReviewType === currentPriority
  );
  
  return assignment?.Assignee || null;
};

const formatSettleDateForTooltip = (value) => {
  if (value == null || value === '') return '';
  const parsed = new Date(String(value).trim().replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const appendStipActor = (tooltip, entry) => {
  if (!entry) return tooltip;
  const who = getUserNameById(entry.negotiator);
  const when = formatSettleDateForTooltip(entry.lastUpdated);
  const sent = entry.status === 'SentForCounterSig';
  const parts = [tooltip];
  if (who && who !== 'Unassigned') parts.push(sent ? `Sent by: ${who}` : `By: ${who}`);
  if (when) parts.push(sent ? `Sent: ${when}` : `Updated: ${when}`);
  return parts.filter(Boolean).join('\n');
};

const appendOfferActor = (tooltip, offer) => {
  if (!offer) return tooltip;
  const who = getUserNameById(offer.Negotiator);
  const when = formatCourtDateDisplayDay(offer.OfferDate);
  const sent = offer.LiveOffer === 1;
  const parts = [tooltip];
  if (who && who !== 'Unassigned') parts.push(sent ? `Sent by: ${who}` : `By: ${who}`);
  if (when) parts.push(sent ? `Sent: ${when}` : `Updated: ${when}`);
  return parts.filter(Boolean).join('\n');
};

const SCAR_DECISION_WIN_ACTIONS = new Set(['S', 'ST', 'NM']);
/** Not a win or loss. D stays open (Awaiting Decision). AH is settled as ADMIN HOLD. */
const SCAR_DECISION_PENDING_ACTIONS = new Set(['D', 'AH']);

const isScarCourtDecision = (caseItem) => {
  const detDate = (isMasterPetitionCase(caseItem) && caseItem?.Art7DeterminationDate)
    ? caseItem.Art7DeterminationDate
    : caseItem?.SCARDeterminationDate;
  if (detDate == null || detDate === '') return false;
  const officer = caseItem?.SCARHearingOfficer;
  const officerStr = officer == null ? '' : String(officer).trim();
  return officerStr !== '' && officerStr !== 'Unassigned';
};

const SETTLED_STATUS_COLOR = '#000000';
const DECISION_WON_COLOR = '#1b5e20';
const DECISION_LOST_COLOR = '#c62828';

const getScarDecisionResult = (caseItem) => {
  const action = getEffectiveDeterminationAction(caseItem);
  if (!action || SCAR_DECISION_PENDING_ACTIONS.has(action)) return null;
  return SCAR_DECISION_WIN_ACTIONS.has(action) ? 'Won' : 'Lost';
};

const getScarSettledStatusColor = (caseItem) => {
  const result = getScarDecisionResult(caseItem);
  if (!result) return SETTLED_STATUS_COLOR;
  return result === 'Won' ? DECISION_WON_COLOR : DECISION_LOST_COLOR;
};

/** Prefer courtfiles/API negotiator name, then negotiation assignee lookup, then case Negotiator id. */
const getNegotiatorLabelForTooltip = (caseItem) => {
  const named = caseItem?.NegotiatorName != null && String(caseItem.NegotiatorName).trim();
  if (named) return String(caseItem.NegotiatorName).trim();
  const fromAssignee = getUserNameById(getCurrentAssignee(caseItem, 'Negotiation'));
  if (fromAssignee && fromAssignee !== 'Unassigned') return fromAssignee;
  return getUserNameById(caseItem?.Negotiator);
};

/** Default settled label/tooltip; uses court decision when determination date + hearing officer present. */
const buildScarSettledStatusInfo = (caseItem) => {
  const who = getNegotiatorLabelForTooltip(caseItem);
  const result = getScarDecisionResult(caseItem);
  const statusColor = getScarSettledStatusColor(caseItem);
  const masterPetition = isMasterPetitionCase(caseItem);
  const useArt7 = masterPetition && (caseItem.Art7DeterminationDate || caseItem.Art7SettleDate);
  const determinationDate = useArt7
    ? caseItem.Art7DeterminationDate
    : caseItem.SCARDeterminationDate;
  const settleDate = useArt7 ? caseItem.Art7SettleDate : caseItem.SCARSettleDate;

  if (isScarCourtDecision(caseItem) && result) {
    const when = formatSettleDateForTooltip(determinationDate);
    const parts = [`Court decision: ${result}`];
    if (when) parts.push(`Date: ${when}`);
    parts.push(`Hearing officer: ${String(caseItem.SCARHearingOfficer).trim()}`);
    if (who && who !== 'Unassigned') parts.push(`Negotiator: ${who}`);
    return {
      statusDisplay: `Decision ${result}`,
      statusTooltip: parts.join('\n'),
      statusColor,
    };
  }
  const when = formatSettleDateForTooltip(settleDate || determinationDate);
  const parts = [useArt7 ? 'Art 7 case settled' : 'Case settled'];
  if (when) parts.push(`Date: ${when}`);
  if (who && who !== 'Unassigned') parts.push(`By: ${who}`);
  return {
    statusDisplay: 'Settled',
    statusTooltip: parts.join('\n'),
    statusColor,
  };
};

export const getStatusInfo = (caseItem, getNegotiationObj) => {
  // Keep the real status. An open doc request adds the purple info icon and its details to the hover.
  const finish = (status) => {
    const docText = openDocRequestTooltipText(caseItem?.DocRequests);
    if (!docText) return status;
    return {
      ...status,
      statusIcon: <InfoIcon sx={{ color: '#5b21b6', fontSize: 16 }} />,
      statusTooltip: [status.statusTooltip, docText].filter(Boolean).join('\n\u00a0\n'),
    };
  };

  const masterPetition = isMasterPetitionCase(caseItem);
  let statusDisplay = 'Not Filed';
  if (masterPetition) {
    statusDisplay = getEffectiveDeterminationAction(caseItem) ? 'Art 7' : 'Art 7 Filed (MP)';
  } else if (caseItem.SCARFiled === 1) {
    statusDisplay = 'SCAR Filed';
  } else if (caseItem.BARFiled === 1) {
    statusDisplay = 'BAR Filed';
  }
  let statusIcon = null;
  let statusTooltip = '';
  let statusStyle = { fontSize: '0.75rem' };

  if (isAdminHoldDetermination(caseItem)) {
    const determinationDate = masterPetition
      ? (caseItem.Art7DeterminationDate || caseItem.Art7SettleDate)
      : (caseItem.SCARDeterminationDate || caseItem.SCARSettleDate || caseItem.BARDeterminationDate || caseItem.BARSettleDate);
    const when = formatSettleDateForTooltip(determinationDate);
    const parts = ['Administrative hold'];
    if (when) parts.push(`Date: ${when}`);
    statusDisplay = 'ADMIN HOLD';
    statusIcon = null;
    statusTooltip = parts.join('\n');
    statusStyle = { ...statusStyle, color: '#000000', fontWeight: 'bold' };
    return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
  }

    // PRIORITY 1: Not filed takes precedence over missing data/review (SCAR/BAR only)
    if (!masterPetition && caseItem.BARFiled !== 1) {
      statusDisplay = 'Not BAR Filed';
      statusIcon = null;
      statusTooltip = 'Case not BAR filed';
      statusStyle = { ...statusStyle, color: '#666666', fontWeight: 'bold' };
      return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
    }
    
  // PRIORITY 2: Explicit BAR settlement. Master Petition cannot settle at BAR.
  if (!masterPetition && caseItem.BARSettleDate) {
    const negotiatorName = getNegotiatorLabelForTooltip(caseItem);
    statusDisplay = 'BAR Settled';
    statusIcon = null;
    statusTooltip = `Case settled at BAR by ${negotiatorName}.`;
    statusStyle = { ...statusStyle, color: '#1b5e20', fontWeight: 'bold' };
    return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
  }

  // PRIORITY 3: Awaiting decision (before settled — action D is not a loss)
  if (isAwaitingDecisionCase(caseItem)) {
    statusDisplay = 'Awaiting Decision';
    statusIcon = null;
    statusTooltip = 'Awaiting decision';
    statusStyle = { ...statusStyle, color: '#000000', fontWeight: 'bold' };
    return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
  }

  // PRIORITY 4: Check if case is settled by traditional logic
  if (isCaseSettled(caseItem)) {
    // First check stipulation status fields for more specific status
    const activeStip = getActiveStip(caseItem);
    const stipStatus = activeStip?.status || '';
    const isBAROnly = isBarOnlyCase(caseItem);
    
    
    if (stipStatus) {
      let stipStatusDisplay = '';
      let stipStatusTooltip = '';
      let stipStatusIcon = null;
      
      switch (stipStatus) {
        case 'Agreed':
          stipStatusDisplay = (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              lineHeight: '1.1',
              textAlign: 'center',
              width: '100%',
              flex: '1',
              minWidth: '100%'
            }}>
              <div style={{ textAlign: 'center', width: '100%', flex: '1' }}>Agreed</div>
            </div>
          );
          stipStatusTooltip = 'Stipulation NOT YET SENT for countersignature.';
          stipStatusIcon = <HandshakeIcon sx={{ color: '#9ccc65', fontSize: 16 }} />; // Yellow-green
          break;
        case 'SentForCounterSig':
          stipStatusDisplay = (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              lineHeight: '1.1',
              textAlign: 'center',
              width: '100%',
              flex: '1',
              minWidth: '100%'
            }}>
              <div style={{ textAlign: 'center', width: '100%', flex: '1' }}>Awaiting</div>
              <div style={{ textAlign: 'center', width: '100%', flex: '1' }}>Countersign</div>
            </div>
          );
          stipStatusTooltip = 'Stipulation has been sent to countersign.';
          stipStatusIcon = <SendIcon sx={{ color: '#4caf50', fontSize: 16 }} />; // Medium green - using Send icon
          break;
        case 'NyscefUploaded': {
          const parcelId = caseItem?.parcel_id ?? '';
          const isVillage = String(parcelId).startsWith('W');
          stipStatusDisplay = (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              textAlign: 'center',
              width: '100%',
              flex: '1',
              minWidth: '100%'
            }}>
              <div style={{ textAlign: 'center', width: '100%', flex: '1' }}>Settled</div>
            </div>
          );
          stipStatusTooltip = isVillage
            ? 'Finalized. Emailed to the SCAR clerk (no NYSCEF upload).'
            : 'Finalized, and uploaded to NYSCEF.';
          stipStatusIcon = <CheckCircleIcon sx={{ color: '#1b5e20', fontSize: 16 }} />; // Dark green
          break;
        }
        case 'Countersigned':
          stipStatusDisplay = (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              textAlign: 'center',
              width: '100%',
              flex: '1',
              minWidth: '100%'
            }}>
              <div style={{ textAlign: 'center', width: '100%', flex: '1' }}>Countersigned</div>
            </div>
          );
          stipStatusTooltip = 'Received countersigned copy, THIS NEEDS TO BE UPLOADED TO NYSCEF TO FINALIZE.';
          stipStatusIcon = <InfoIcon sx={{ color: '#4caf50', fontSize: 16 }} />; // Medium green
          break;
        case 'SoOrdered': {
          stipStatusDisplay = (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              textAlign: 'center',
              width: '100%',
              flex: '1',
              minWidth: '100%'
            }}>
              <div style={{ textAlign: 'center', width: '100%', flex: '1' }}>So Ordered</div>
            </div>
          );
          const soOrderedDate = formatSettleDateForTooltip(
            (masterPetition && caseItem.Art7DeterminationDate)
              ? caseItem.Art7DeterminationDate
              : (isBAROnly ? caseItem.BARDeterminationDate : caseItem.SCARDeterminationDate)
          );
          const soOrderedParts = ['Stip has been processed, and is so ordered on Nyscef'];
          if (soOrderedDate) soOrderedParts.push(`Date: ${soOrderedDate}`);
          stipStatusTooltip = soOrderedParts.join('\n');
          stipStatusIcon = <CheckCircleIcon sx={{ color: '#0d4f0d', fontSize: 16 }} />;
          break;
        }
        default: {
          let statusColor = '#1b5e20';
          if (isBAROnly) {
            stipStatusDisplay = 'Settled (BAR)';
            const who = getNegotiatorLabelForTooltip(caseItem);
            const when = formatSettleDateForTooltip(caseItem.BARSettleDate || caseItem.BARDeterminationDate);
            const parts = ['Case settled - BAR only (no SCAR filing)'];
            if (when) parts.push(`Date: ${when}`);
            if (who && who !== 'Unassigned') parts.push(`By: ${who}`);
            stipStatusTooltip = parts.join('\n');
          } else {
            const scarSettled = buildScarSettledStatusInfo(caseItem);
            stipStatusDisplay = scarSettled.statusDisplay;
            stipStatusTooltip = scarSettled.statusTooltip;
            statusColor = scarSettled.statusColor;
          }
          stipStatusIcon = null;
          statusDisplay = stipStatusDisplay;
          statusIcon = stipStatusIcon;
          statusTooltip = appendStipActor(stipStatusTooltip, activeStip);
          statusStyle = { ...statusStyle, color: statusColor, fontWeight: 'bold' };
          return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
        }
      }
      
      // Set progressive color scheme for status text
      let statusColor = '#1b5e20'; // Default dark green
      switch (stipStatus) {
        case 'Agreed':
          statusColor = '#9ccc65'; // Yellow-green
          break;
        case 'SentForCounterSig':
          statusColor = '#4caf50'; // Medium green
          break;
        case 'Countersigned':
        case 'NyscefUploaded':
          statusColor = '#1b5e20'; // Dark green
          break;
        case 'SoOrdered':
          statusColor = '#0d4f0d'; // Darkest green
          break;
        default:
          statusColor = '#1b5e20'; // Default dark green
      }
      
      statusDisplay = stipStatusDisplay;
      statusIcon = stipStatusIcon;
      statusTooltip = appendStipActor(stipStatusTooltip, activeStip);
      statusStyle = { ...statusStyle, color: statusColor, fontWeight: 'bold' };
      return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
    }
    
    // Fall back to original logic if no stipulation status - NO CHECKMARKS
    if (isBAROnly) {
      statusDisplay = 'Settled (BAR)';
      statusIcon = null; // No checkmark for generic settled status
      const who = getNegotiatorLabelForTooltip(caseItem);
      const when = formatSettleDateForTooltip(caseItem.BARSettleDate || caseItem.BARDeterminationDate);
      const parts = ['Case settled - BAR only (no SCAR filing)'];
      if (when) parts.push(`Date: ${when}`);
      if (who && who !== 'Unassigned') parts.push(`By: ${who}`);
      statusTooltip = parts.join('\n');
      statusStyle = { ...statusStyle, color: '#1b5e20', fontWeight: 'bold' };
      return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
    } else {
      statusIcon = null; // No checkmark for generic settled status
      const scarSettled = buildScarSettledStatusInfo(caseItem);
      statusDisplay = scarSettled.statusDisplay;
      statusTooltip = scarSettled.statusTooltip;
      statusStyle = { ...statusStyle, color: scarSettled.statusColor, fontWeight: 'bold' };
      return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
    }
  }

  // PRIORITY 5: Aventine files uploaded to NYSCEF. A SCAR Petition alone is not uploaded evidence.
  const aventineEvidence = filterNyscefFilesByParty(caseItem?.NyscefS3Files, EVIDENCE_PARTIES.AVENTINE)
    .filter((file, index) => {
      const exhibitName = `${displayFileLabel(file, index)} ${getNyscefRawFilename(file)}`.toLowerCase().replace(/[_-]+/g, ' ');
      return !exhibitName.includes('scar petition');
    });
  if (aventineEvidence.length) {
    statusDisplay = `Uploaded Evid. (${aventineEvidence.length})`;
    statusIcon = null;
    statusTooltip = ['NYSCEF uploaded evidence:', ...aventineEvidence.map((file, index) => displayFileLabel(file, index))].join('\n');
    statusStyle = { ...statusStyle, color: '#9ccc65', fontWeight: 'bold' };
    return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
  }

  // PRIORITY 6: Check if case has multiple negotiations (Negotiating status)
  const negotiations = caseItem.Negotiations || [];
  if (negotiations && Array.isArray(negotiations) && negotiations[0]?.offers.length > 1) {
    // Only show "Negotiating" if there's actually a live offer (not cancelled)
    const hasLiveOffer = isOfferSent(negotiations, caseItem.parcel_id);
    if (hasLiveOffer) {
      // Check if there's a town offer in the most recent round
      const hasTownOffer = hasTownOfferInMostRecentRound(negotiations);
      
      if (hasTownOffer) {
        // If there IS a town offer, show "Negotiating"
        statusDisplay = 'Negotiating';
        statusIcon = null; // Remove the dropdown caret
        statusTooltip = `Multiple negotiations in progress (${negotiations.length} entries)`;
        statusStyle = { ...statusStyle, color: '#e65100', fontWeight: 'bold' }; // More orange color
        return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
      } 

    }
  }


  // PRIORITY 7: Check if case is flagged to not file at SCAR (BAR Only)
  if (!masterPetition && caseItem.SCARFiled === 0) {
    statusDisplay = 'BAR Only';
    statusIcon = null; // No status icon for BAR Only cases
    statusTooltip = 'Case flagged to not file at SCAR - BAR only';
    statusStyle = { ...statusStyle, color: '#666666', fontWeight: 'bold' };
    return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
  }

  const dataCleaningComplete = isAssignmentComplete(caseItem, 'DataCleaning');
  const caseReviewComplete = isAssignmentComplete(caseItem, 'CaseReview');
  const isOfferSentStatus = isOfferSent(negotiations, caseItem.parcel_id);

  if (isOfferSentStatus) {
    const negotiationCase = getNegotiationObj.cases.find(c => c.parcel_id === caseItem.parcel_id);
    const hasOffer = negotiationCase && negotiationCase.OfferValue !== null && negotiationCase.OfferValue !== 'None' && negotiationCase.OfferValue !== '' && !isNaN(Number(negotiationCase.OfferValue));
    if (hasOffer) {
      const latestAventineOffer = getMostRecentOffer(negotiations, 'Aventine')?.offer;
      return finish({
        statusDisplay: 'Offer Sent',
        statusIcon: <SendIcon sx={{ color: '#4caf50', fontSize: 16 }} />,
        statusTooltip: appendOfferActor('Offer has been finalized and sent', latestAventineOffer),
        statusStyle: { ...statusStyle, fontWeight: 'bold' },
      });
    }
  }

  if (!dataCleaningComplete) {
    const assignee = getCurrentAssignee(caseItem, 'DataCleaning');
    return finish({
      statusDisplay: 'Missing Data',
      statusIcon: null,
      statusTooltip: `Missing Data\nAssigned to: ${getUserNameById(assignee) || 'Unassigned'}`,
      statusStyle: { ...statusStyle, color: '#ff6f00', fontWeight: 'bold' },
    });
  }

  if (!caseReviewComplete) {
    const assignee = getCurrentAssignee(caseItem, 'CaseReview');
    return finish({
      statusDisplay: 'Needs Review',
      statusIcon: null,
      statusTooltip: `Needs Review\nAssigned to: ${getUserNameById(assignee) || 'Unassigned'}`,
      statusStyle: { ...statusStyle, color: '#1976d2', fontWeight: 'bold' },
    });
  }

  const hasUpdatedOfferStatus = hasUpdatedOffer(negotiations, caseItem.parcel_id);
  if (hasUpdatedOfferStatus) {
    const negotiationCase = getNegotiationObj.cases.find(c => c.parcel_id === caseItem.parcel_id);
    const hasOffer = negotiationCase && negotiationCase.OfferValue !== null && negotiationCase.OfferValue !== 'None' && negotiationCase.OfferValue !== '' && !isNaN(Number(negotiationCase.OfferValue));
    if (hasOffer) {
      const latestAventineOffer = getMostRecentOffer(negotiations, 'Aventine')?.offer;
      return finish({
        statusDisplay: 'Updated Offer',
        statusIcon: null,
        statusTooltip: appendOfferActor('Offer has been updated but not yet sent', latestAventineOffer),
        statusStyle: { ...statusStyle, fontWeight: 'bold' },
        statusSubtext: '(not sent)',
      });
    }
  }

  if (caseReviewComplete) {
    const assignee = getCurrentAssignee(caseItem, 'CaseReview');
    return finish({
      statusDisplay: 'Offer Finalized',
      statusIcon: null,
      statusTooltip: `Ready to send\nAssigned to: ${getUserNameById(assignee) || 'Unassigned'}`,
      statusStyle: { ...statusStyle, fontWeight: 'bold' },
    });
  }

  const assignee = getCurrentAssignee(caseItem, 'CaseReview') ||
                   getCurrentAssignee(caseItem, 'DataCleaning') ||
                   getCurrentAssignee(caseItem, 'Negotiation');
  statusIcon = null;
  statusTooltip = `Pending\nAssigned to: ${getUserNameById(assignee) || 'Unassigned'}`;

  return finish({ statusDisplay, statusIcon, statusTooltip, statusStyle });
};


// DRY helpers for reduction calculations
const calculateReductionPercentMV = (offerMarketValue, indicatedFullMarketValue) => {
  if (!offerMarketValue || !indicatedFullMarketValue) return 0;
  const percentage = ((offerMarketValue - indicatedFullMarketValue) / indicatedFullMarketValue) * 100;
  return Math.abs(percentage) < 0.01 ? 0 : parseFloat(percentage.toFixed(2));
};

// Helper function to calculate pctReduction
export const calculatePctReduction = (currentCase) => {
  const ifmv = getIFMVUtil(currentCase);
  if (!ifmv) return 0;
  if (currentCase.OfferValue) {
    const eq = currentCase.EQ;
    const rar = currentCase.RAR;
    const loa = getConversionFactor(eq, rar);
    const offerMV = loa > 0 ? currentCase.OfferValue / loa : currentCase.OfferValue;
    return calculateReductionPercentMV(offerMV, ifmv);
  }
  if (currentCase.SubjectMarketValue) {
    return calculateReductionPercentMV(currentCase.SubjectMarketValue, ifmv);
  }
  return 0;
};

const schoolDistrictNameFromCase = (processedCase, compSheet) => {
  const first = (v) => {
    const raw = Array.isArray(v) ? v[0] : v;
    const s = String(raw ?? '').trim();
    return s && s.toLowerCase() !== 'none' ? s : '';
  };
  return first(processedCase?.SDName) || first(compSheet?.SDName) || '';
};

// Main row creation function
export const createRows = (
  cases,
  fullNegotiationCases,
  {
    townView,
    village,
    getCompSheet,
    getNegotiationObj,
    getMunicipalityFromParcelId,
    reportType = 'standard',
    getCaseNotes,
  }
) => {
  let sequentialIndex = 0;
  const compAddressByPid = getNegotiationObj?.compAddressByPid || {};
  
  // Note: Offer adjustments are now handled by the Generate Portfolio Offer button
  // This service uses the adjustment data passed from the parent component
  // const runType = getNegotiationObj.runType;
  
  return cases.map((currentCase, index) => {
    // Get the updated case data from the store to ensure we have the latest OfferValue and assignment info
    const updatedCase = getNegotiationObj.cases.find(
      (storeCase) => storeCase.parcel_id === currentCase.parcel_id
    );

    // Use updated case if available, otherwise fall back to current case
    const processedCase = updatedCase || currentCase;
    const negotiations = processedCase.Negotiations || [];
    const caseSettled = isCaseSettled(processedCase);
    const awaitingDecision = isAwaitingDecisionCase(processedCase);
    const adminHold = isAdminHoldDetermination(processedCase);
    const stipFinalized = isStipFinalizedCase(processedCase);
    const notesDisabledReason = getNotesDisabledReason({
      awaitingDecision,
      stipFinalized,
      caseSettled,
      adminHold,
    });
    const aventineLatestOfferSent = isOfferSent(negotiations);
    const townWorkupNotAvailable = townView && !wereAnyOffersSent(negotiations) && !caseSettled;
    // Calculate barFiled and scarFiled for row data
    const barFiled = processedCase.BARFiled === 1;
    const scarFiled = processedCase.SCARFiled !== 0;
    const masterPetition = isMasterPetitionCase(processedCase);
    
    // Live only when unsettled and either BAR filed or a master petition. Not BAR filed is not in play.
    const isActive = !isCaseSettled(processedCase) && !stipFinalized && (barFiled || masterPetition);
    const townAventineDisplayNotAvailable = isActive && townWorkupNotAvailable;

    // Note: Filtering logic has been moved to SummaryCaseReviewTableRefactored.js
    // This function now only creates rows for cases that are passed to it


    
    const fullIndex = fullNegotiationCases.findIndex(
      (c) => c.parcel_id === processedCase.parcel_id
    );
    const enhancedCase = fullIndex > -1 ? fullNegotiationCases[fullIndex] : null;
    
    // Find index in getNegotiationObj.cases to match with getCaseNotes array
    const caseIndex = getNegotiationObj?.cases?.findIndex(
      (c) => c.parcel_id === processedCase.parcel_id
    ) ?? -1;
    
    // Get CaseNotes from store if available, otherwise fall back to processedCase
    const caseNotes = (caseIndex >= 0 && getCaseNotes?.[caseIndex] !== undefined) 
      ? getCaseNotes[caseIndex] 
      : processedCase.CaseNotes || '';

    const compSheetEntry = Array.isArray(getCompSheet) && getCompSheet.length > fullIndex ? getCompSheet[fullIndex] : null;
    const summaryMappedFields = (sheet) => {
      const extra = {};
      for (const { field, dbField, summary } of CASE_REVIEW_COLUMNS) {
        if (!summary || !dbField) continue;
        const column = sheet?.[dbField];
        extra[field] = processedCase[dbField] ?? (Array.isArray(column) ? column[0] : undefined) ?? '';
      }
      return extra;
    };

    const compIds = savedCompIds(processedCase.Comps);
    const displayComp = (id) => (townView ? (compAddressByPid[id] || '') : (id || ''));
    const [comp1 = '', comp2 = '', comp3 = '', comp4 = '', comp5 = ''] = compIds.map(displayComp);

    const villageRun = getNegotiationObj?.villageRun === 1 || getNegotiationObj?.villageRun === true;
    const tentativeField = villageRun ? 'TentativeAssessmentVillage' : 'TentativeAssessmentTown';
    const tentativeColumn = compSheetEntry?.[tentativeField];
    const tentativeRaw = processedCase[tentativeField] ?? (Array.isArray(tentativeColumn) ? tentativeColumn[0] : tentativeColumn);
    const tentativeAssessment = Number(tentativeRaw);
    const barSettled = processedCase.BARSettleDate != null && processedCase.BARSettleDate !== '';
    const propertyAssessment = barSettled && Number.isFinite(tentativeAssessment) && tentativeAssessment > 0
      ? tentativeAssessment
      : (getAVUtil(processedCase) ?? getAVUtil(compSheetEntry, 0));

    // For town view, we don't need comp sheets, so don't treat missing comp sheets as loading
    const isLoading = !townView && !compSheetEntry;
    // Return a row with loading state if data isn't ready
    if (isLoading) {
      const loadingRow = {
        id: processedCase.parcel_id,
        Comp: sequentialIndex + 1,
        loading: true,
        Address: processedCase.Address || '',
        PhotoURL: processedCase.PhotoURL || '',
        MLSPhotoFolder: processedCase.MLSPhotoFolder || compSheetEntry?.MLSPhotoFolder?.[0] || '',
        parcel_id: processedCase.parcel_id || '',
        ClientName: processedCase.ClientName || '',
        Muni: getMunicipalityFromParcelId(processedCase.parcel_id),
        Town: processedCase.Town || '',
        ZipCode: processedCase.Zip5 || '',
        PropertyAssessment: propertyAssessment,
        EQ: processedCase.EQ,
        OfferValue: 'Loading...',
        OfferValueStyle: { color: '#888', fontStyle: 'italic' },
        IFMV: getIFMVUtil(processedCase) ?? getIFMVUtil(compSheetEntry, 0),
        SCARIndexNumber: processedCase.SCARIndexNumber || '',
        SCARHearingDate: processedCase.SCARHearingDate ?? null,
        SDName: schoolDistrictNameFromCase(processedCase, compSheetEntry),
        ProprietyWorkup: 0,
        SalePrice: 0,
        SaleDate: '',
        RecentSale: false,
        RAR: processedCase.RAR,
        RepID: processedCase.RepID || '',
        SCARDeterminationAction: '',
        SCARDeterminationValue: 'Loading...',
        statusDisplay: 'Loading...',
        statusStyle: { color: '#888', fontStyle: 'italic' },
        statusIcon: null,
        statusTooltip: 'Data is being loaded...',
        Comp1: '',
        Comp2: '',
        Comp3: '',
        Comp4: '',
        Comp5: '',
        CompsCount: compIds.length,
        CaseNotes: '',

        _isSettled: caseSettled,
        AwaitingDecision: awaitingDecision,
        StipFinalized: stipFinalized,
        notesDisabledReason,
        isActive: isActive,
        CaseAssignmentInfo: processedCase.CaseAssignmentInfo,
        notFiledAtScar: !masterPetition && processedCase.SCARFiled === 0,
        barFiled: processedCase.BARFiled === 1,
        MasterPetition: masterPetition,
        scarFiled: processedCase.SCARFiled !== 0,
        SCARFiled: processedCase.SCARFiled,
        StipInfo: processedCase.StipInfo ?? null,
        TownResponse: '',
        TownCounter: '',
        TownNotes: '',
        impliedReduction: 0,
        pctReduction: 0,
        RunDate: processedCase.RunDate ?? null,
        ManualReview: processedCase.ManualReview ?? null,
        Negotiations: negotiations,
        _aventineLatestOfferSent: aventineLatestOfferSent,
        _townWorkupNotAvailable: townWorkupNotAvailable,
        _townAventineDisplayNotAvailable: townAventineDisplayNotAvailable,
      };

      sequentialIndex++;
      return { ...loadingRow, ...summaryMappedFields(compSheetEntry) };
    }

    // Calculate Propriety Workup using unified function
    let proprietyWorkup = 0;
    let impliedReduction = 0;

    const compSheet = compSheetEntry;

    let workupResult;
    if (townView) {
      proprietyWorkup = workupValueFromSubjectMarketValue(processedCase, compSheet);
      workupResult = proprietyWorkup;
    } else {
      workupResult = calculateProprietyWorkup(processedCase, compSheet);
      proprietyWorkup = workupResult === 'loading' ? 0 : workupResult;
    }

    // A row is loading ONLY if comp-based workup is still pending (not used in town view)
    const isRowLoading = !townView && workupResult === 'loading';

    // Calculate experimental weighted averages
    let top5Average = 0;
    let top10WeightedAverage = 0;
    let top10ExponentialWeightedAverage = 0;
    let top10LinearWeightedAverage = 0;
    
    if (compSheet) {
      // Get all comp prices for top 5 average
      const allCompPrices = [];
      const maxComps = Math.min(10, compSheet.parcel_id?.length || 0);
      
      for (let i = 0; i < maxComps; i++) {
        const adjPrice = compSheet.adj_price?.[i];
        if (adjPrice !== undefined && adjPrice !== null && !isNaN(parseFloat(adjPrice))) {
          allCompPrices.push(parseFloat(adjPrice));
        }
      }
      
      // Calculate top 5 average (same concept as selected comp average)
      top5Average = calculateTop5Average(allCompPrices);
      
      // Calculate weighted average for top 10 using unified function
      top10WeightedAverage = calculateWeightedAverage(allCompPrices);
      // Keep the other calculations for backward compatibility if needed
      top10ExponentialWeightedAverage = calculateWeightedAverage(allCompPrices);
      top10LinearWeightedAverage = calculateWeightedAverage(allCompPrices);
    }

    const ifmv = getIFMVUtil(processedCase) ?? getIFMVUtil(compSheet, 0);

    if (proprietyWorkup && ifmv) {
      impliedReduction = ((proprietyWorkup - ifmv) / ifmv) * 100;
    }

    // Rest of the row creation logic
    const isSCARFiled = Boolean(processedCase.RepID) || processedCase.SCARFiled === 1;
    const SCARDeterminationValue = masterPetition
      ? processedCase.Art7DeterminationValue || 0
      : isSCARFiled
        ? processedCase.SCARDeterminationValue || 0
        : processedCase.BARDeterminationValue || 0;

    // Use the unified settlement info helper to get settlement value
    const settlementInfo = getSettlementInfo(processedCase);
    const settlementValue = settlementInfo.settlementValue || (masterPetition
      ? Math.round(processedCase.Art7DeterminationValue) || 0
      : Math.round(processedCase.SCARDeterminationValue) || Math.round(processedCase.BARDeterminationValue) || 0);
      const conversionFactor = getConversionFactor(processedCase.EQ, processedCase.RAR);

      const MVSettlementValue = conversionFactor > 0 ? Math.round(settlementValue / conversionFactor / 10) * 10 : settlementValue;
    
    // Calculate percent reduction based on settlement value if case is inactive, otherwise use standard logic
    let pctReduction;
    if (!isActive && MVSettlementValue > 0) {
      pctReduction = calculateReductionPercentMV(MVSettlementValue, ifmv);
    } else {
      pctReduction = calculatePctReduction({ ...processedCase, IFMV: ifmv });
    }


    
    const { statusDisplay, statusStyle, statusIcon, statusTooltip, statusSubtext } = getStatusInfo(
      processedCase,
      getNegotiationObj
    );

    // Get the most recent offer values from negotiations
    const mostRecentAventineOffer = getMostRecentOfferValue(negotiations, 'Aventine');
    const mostRecentTownOffer = getMostRecentOfferValue(negotiations, 'Town');
    
    // Use most recent values from negotiations, fallback to case-level values
    // For settled cases, use the settlement value in AV terms (consistent with active cases)
    let offerValue;
    if (!isActive && settlementValue > 0) {
      // For settled cases, use the settlement value in AV terms (same as active cases)
      // The column display logic will handle AV/MV conversion based on avToggleActive
      offerValue = settlementValue;
    } else {
      // For active cases, use the most recent offer
      offerValue = mostRecentAventineOffer !== null ? mostRecentAventineOffer : (processedCase.OfferValue || null);
    }

    // Town view, active: align row values with live Aventine rule (same as workup — wereAnyOffersSent)
    if (townView && isActive) {
      if (!wereAnyOffersSent(negotiations)) {
        offerValue = null;
      } else if (!isOfferSent(negotiations)) {
        const liveAv = getMostRecentOfferValue(negotiations, 'Aventine', true);
        if (liveAv != null && Number.isFinite(liveAv) && liveAv > 0) offerValue = liveAv;
      }
    }

    let townOffer = mostRecentTownOffer !== null ? mostRecentTownOffer : (processedCase.TownOffer || null);
    let offerValueStyle = {};
    
    // Calculate Requested AV value - now that we store AV values, use them directly
    let requestedAV = null;
    
    // Calculate Requested AV from the offer value
    if (offerValue !== null && offerValue !== undefined && offerValue > 0) {
      requestedAV = Math.round(offerValue);
    } else if (!isActive && settlementValue && settlementValue > 0) {
      // For settled cases, use settlement value as RequestedAV if no offer value available
      requestedAV = Math.round(settlementValue);
    }

    // Calculate Portfolio Requested AV value - now that we store AV values, use them directly
    let portfolioRequestedAV = null;
    const portfolioOfferValue = processedCase.PortfolioOffer;
    
    // If we have a stored PortfolioRequestedAV, use it; otherwise use PortfolioOffer directly
    if (processedCase.PortfolioRequestedAV !== undefined && processedCase.PortfolioRequestedAV !== null) {
      portfolioRequestedAV = processedCase.PortfolioRequestedAV;
    } else if (portfolioOfferValue) {
      // Since we now store AV values, use PortfolioOffer directly as PortfolioRequestedAV
      portfolioRequestedAV = Math.round(portfolioOfferValue);
    }

    // Calculate Town Req AV value - functions exactly like Requested AV but for Town offers
    let townReqAV = null;
    let townPctReduction = null;
    
    // Calculate Town Req AV from the town offer value
    if (townOffer !== null && townOffer !== undefined && townOffer > 0) {
      townReqAV = Math.round(townOffer);
      
      // Calculate town percentage reduction (same logic as pctReduction - compare against Property Assessment)
      if (propertyAssessment && propertyAssessment > 0) {
        townPctReduction = ((townReqAV - propertyAssessment) / propertyAssessment) * 100;
      }
    }

    // Calculate Offer Diff - difference between town offer and our offer (store as AV difference)
    let offerDiff = null;
    if (townOffer !== null && townOffer !== undefined && townOffer > 0 && 
        offerValue !== null && offerValue !== undefined && offerValue > 0) {
      
      // Store the AV difference - the column display logic will handle conversion
      offerDiff = townOffer - offerValue;
    }
    
    // Calculate case strength if we have comp sheet data
    // Use caseIndex (original case index) to get the correct case and compSheet
    // The CaseStrength from enhancedCaseData is used for sorting, but we recalculate here
    // to ensure it matches the case's actual data (in case data was updated)
    let caseStrengthPercentage = processedCase.CaseStrength ?? null;
    let caseStrengthColor = processedCase.CaseStrengthColor ?? null;
    
    // Recalculate if we have the necessary data and caseIndex is valid
    // Use compSheet IFMV instead of processedCase IFMV since compSheet is the source of truth
    const compSheetIFMV = getIFMVUtil(compSheet, 0);
    if (compSheet && compSheetIFMV && caseIndex >= 0) {
      try {
        // Use the simplified utility function, passing reportType for proper field selection
        // Use caseIndex (original case index) to look up the correct case and compSheet
        const caseStrengthData = getCaseStrengthPercentage(caseIndex, reportType);
        caseStrengthPercentage = caseStrengthData.caseStrengthA;
        caseStrengthColor = getCaseStrengthColor(caseStrengthPercentage);
      } catch (error) {
        logger.error('Error calculating case strength:', error);
        // Keep the values from enhancedCaseData if calculation fails
      }
    }
    
    // Get Address with fallbacks: processedCase.Address -> compSheet.Address[0] -> parcel_id
    let address = processedCase.Address || '';
    if (!address && compSheet && Array.isArray(compSheet.Address) && compSheet.Address.length > 0) {
      address = compSheet.Address[0] || '';
    }
    if (!address) {
      address = processedCase.parcel_id || '';
    }
    
    const resolvedTaxes =
      enhancedCase && enhancedCase.Taxes !== undefined && enhancedCase.Taxes !== null && enhancedCase.Taxes !== ''
        ? enhancedCase.Taxes
        : (processedCase.Taxes ?? null);

    const baseRow = {
      id: processedCase.parcel_id,
      Comp: sequentialIndex + 1,
      // loading: isRowLoading,
      Address: address,
      PhotoURL: processedCase.PhotoURL || '',
      MLSPhotoFolder: processedCase.MLSPhotoFolder || compSheet?.MLSPhotoFolder?.[0] || '',
      parcel_id: processedCase.parcel_id || '',
      ClientName: processedCase.ClientName || '',
      Muni: getMunicipalityFromParcelId(processedCase.parcel_id),
      Town: processedCase.Town || '',
      ZipCode: processedCase.Zip5 || '',
      Taxes: resolvedTaxes,
      PropertyAssessment: propertyAssessment,
      EQ: processedCase.EQ,
      OfferValue: offerValue,
      OfferValueStyle: offerValueStyle, // Add style for BAR Only cases
      PortfolioOffer: processedCase.PortfolioOffer || null, // Use existing Portfolio Offer value
      TownOffer: townOffer, // Add Town Offer field
      RequestedAV: requestedAV, // Add Requested AV field
      TownReqAV: townReqAV, // Add Town Req AV field
      OfferDiff: offerDiff, // Add Offer Diff field
      PortfolioRequestedAV: portfolioRequestedAV, // Add Portfolio Requested AV field
      AlgorithmFirstOffer: processedCase.AlgorithmFirstOffer ?? null,
      AlgorithmSecondOffer: processedCase.AlgorithmSecondOffer ?? null,
      AlgorithmCeilingOffer: processedCase.AlgorithmCeilingOffer ?? null,
      IFMV: ifmv,
      SCARIndexNumber: processedCase.SCARIndexNumber || '',
      SCARHearingDate: processedCase.SCARHearingDate ?? null,
      SDName: schoolDistrictNameFromCase(processedCase, compSheet),
      ProprietyWorkup: isRowLoading ? 'Loading...' : proprietyWorkup,
      Top5Average: top5Average, // Experimental: Top 5 average
      Top10WeightedAverage: top10WeightedAverage, // Experimental: Top 10 inverse weighted average
      Top10ExponentialWeightedAverage: top10ExponentialWeightedAverage, // Experimental: Top 10 exponential weighted average
      Top10LinearWeightedAverage: top10LinearWeightedAverage, // Experimental: Top 10 linear weighted average
      CaseStrength: caseStrengthPercentage, // Case strength percentage
      CaseStrengthColor: caseStrengthColor, // Case strength color for display
      SalePrice: processedCase.SalePrice || 0,
      SaleDate: processedCase.SaleDate || '',
      RecentSale: processedCase.RecentSale || false,
      RAR: processedCase.RAR,
      RepID: processedCase.RepID || '',
      SCARDeterminationAction: processedCase.SCARDeterminationAction || '',
      BARDeterminationAction: processedCase.BARDeterminationAction || '',
      SCARDeterminationValue: SCARDeterminationValue,
      // Add separate numeric fields for determination values
      BARDeterminationValue: processedCase.BARDeterminationValue || 0,
      SCARDeterminationValueNumeric: processedCase.SCARDeterminationValue || 0,
      // Settlement value: prioritize SCAR over BAR
      SettlementValue: settlementValue,
      statusDisplay,
      statusStyle,
      statusIcon,
      statusTooltip,
      statusSubtext,
      Comp1: comp1,
      Comp2: comp2,
      Comp3: comp3,
      Comp4: comp4,
      Comp5: comp5,
      CompsCount: compIds.length,
      CaseNotes: caseNotes,
      pctReduction: pctReduction,
      townPctReduction: townPctReduction,
      impliedReduction: impliedReduction,
      _townAventineDisplayNotAvailable: townAventineDisplayNotAvailable,
      _townWorkupNotAvailable: townWorkupNotAvailable,
      _aventineLatestOfferSent: aventineLatestOfferSent,
      TownResponse: processedCase.TownResponse || '',
      TownCounter: processedCase.TownCounter || '',
      TownNotes: processedCase.TownNotes || '', // Keep for backward compatibility

      _isSettled: caseSettled,
      AwaitingDecision: awaitingDecision,
      StipFinalized: stipFinalized,
      notesDisabledReason,
      isActive: isActive,
      CaseAssignmentInfo: processedCase.CaseAssignmentInfo,
      notFiledAtScar: !masterPetition && processedCase.SCARFiled === 0,
      barFiled: barFiled,
      scarFiled: scarFiled,
      BARFiled: processedCase.BARFiled,
      SCARFiled: processedCase.SCARFiled,
      MasterPetition: processedCase.MasterPetition === 1,
      CommAuth: processedCase.CommAuth === 1 || processedCase.CommAuth === '1' ? 1 : 0,
      SCARSettleDate: processedCase.SCARSettleDate ?? null,
      BARSettleDate: processedCase.BARSettleDate ?? null,
      Art7SettleDate: processedCase.Art7SettleDate ?? null,
      StipInfo: processedCase.StipInfo ?? null,

      // No clue if these work but this got added logically a while ago and it sort of makes sense.
      Art7DeterminationDate: processedCase.Art7DeterminationDate ?? null,
      Art7DeterminationAction: processedCase.Art7DeterminationAction ?? null,
      Art7DeterminationValue: processedCase.Art7DeterminationValue ?? null,

      // Add negotiation data
      Negotiations: negotiations, // Also add uppercase version for compatibility
      
      // Add S3 files for workup visualization
      S3Files: processedCase.S3Files || [],
      NyscefS3Files: processedCase.NyscefS3Files || [],
      DocRequests: processedCase.DocRequests || [],
      RunDate: processedCase.RunDate ?? null,
      ManualReview: processedCase.ManualReview ?? null,
    };

    sequentialIndex++;
    return { ...baseRow, ...summaryMappedFields(compSheet) };
  });
};

// Function to calculate the maximum number of negotiation rounds across all cases
// Counts all rounds that exist, regardless of offer type
export const getMaxNegotiationRounds = (rows) => {
  let maxRounds = 0;
  
  rows.forEach((row, index) => {
    if (row.Negotiations && Array.isArray(row.Negotiations)) {
      
      // Find the highest round number that exists
      let maxRoundNumber = 0;
      
      for (const roundData of row.Negotiations) {
        if (roundData.round && roundData.round > maxRoundNumber) {
          maxRoundNumber = roundData.round;
        }
      }
      
      if (maxRoundNumber > maxRounds) {
        maxRounds = maxRoundNumber;
      }
    }
  });
  
  return maxRounds;
}; 